import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { Submission } from '../../store/submission/types';
import submissionSlice from '../../store/submission/slice';
import {
  selectBidState,
  selectInfo,
  selectKeywordState,
  selectSubmissionState,
  selectTrackRoles,
} from '../../store/selectors';
import { connect } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import { ChairInfo, Info, InfoDeleteDTO, TableFriendlyName } from '../../store/info/types';
import { formatKeywordColumn } from '../../helpers/table';
import MyGridRedux from '../../components/MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import TableHeader from '../../components/TableHeader/TableHeader';
import { Role } from '../../store/conference/types';
import { getRouteByName } from '../../router/routes';
import history from '../../store/history';
import { fillRoutePath } from '../../helpers/path';
import { Bid, BidOption } from '../../store/bid/types';
import { getReviewer, stringifyOrganization } from '../../helpers/users';
import useFetchTableData from '../../store/table/useFetchTableData';
import infoSlice from '../../store/info/slice';

interface Props {
  getSubmissionsAction: () => void;
  submissionsById: { [key: string]: Submission };
  loading: boolean;
  keywordById: { [key: number]: Keyword };
  info: Info;
  roleById: { [key: string]: Role };
  bidOptionsById: { [key: number]: BidOption };
  deleteInfoAction: (data: InfoDeleteDTO) => void;
}

const SubmissionBidsPage = ({
  getSubmissionsAction,
  submissionsById,
  loading,
  keywordById,
  info,
  roleById,
  bidOptionsById,
  deleteInfoAction,
}: Props) => {
  const { t, i18n } = useTranslation();
  const params: any = useParams();
  const id = parseInt(params.id);

  const friendlyName: TableFriendlyName = 'submission_bids';
  const queryParams = { submission_id: id }; // submission_id refers to external_id

  const [fetchData, fetchCustomColumnsData, getInfoAction] = useFetchTableData(friendlyName, queryParams);

  /* Fetch table data */
  useEffect(() => {
    if (Object.keys(submissionsById).length === 0) {
      getSubmissionsAction();
    }
    return () => {
      deleteInfoAction({ friendlyName });
    };
  }, []);

  // Given id must match with external_id field
  const submission = Object.values(submissionsById).find((submission) => submission.external_id == id);

  if (loading) {
    return <Loading />;
  } else if (!submission) {
    return <div>Submission #{id} not found</div>;
  }

  /* Generate data for table */
  const chairInfo = info as ChairInfo;
  const inputData: unknown[] = [];
  const bids = Object.values(info[friendlyName].byId) as Bid[];
  bids.forEach((bid) => {
    const reviewer = getReviewer(bid.person_role_id, chairInfo.users);
    if (reviewer) {
      const newRegister = {
        ...reviewer,
        bid: bid.bid_option_id ? bidOptionsById[bid.bid_option_id].name : undefined,
        conflict: bid.conflict,
      };

      // Add Role description
      newRegister.role = roleById[reviewer.role_id].description;

      formatKeywordColumn(newRegister, keywordById);

      // @ts-ignore
      newRegister.organization = stringifyOrganization(newRegister);

      inputData.push(newRegister);
    }
  });

  const parentRoute = getRouteByName('RouteAllSubmissionsBids');

  return (
    <div className="flex flex-col h-full">
      <TableHeader
        onBack={() => history.push(fillRoutePath(parentRoute.path))}
        onMove={(rowId) => {
          const submission = submissionsById[rowId];
          getInfoAction({ friendlyName, queryParams: { ...queryParams, submission_id: submission.external_id } });
          history.replace(
            fillRoutePath(getRouteByName('RouteSubmissionBids').path, {
              id: submission.external_id.toString(),
            }),
          );
        }}
        type={'App\\Models\\Submission'}
        row={submission}
        keywordById={keywordById}
        roleById={roleById}
      />
      <div className="mt-8" />
      <MyGridRedux friendlyName={friendlyName} inputData={inputData as Record<string, unknown>[]} />
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  submissionsById: selectSubmissionState(state).submissionsById,
  loading: selectSubmissionState(state).loading || selectInfo(state).loading,
  keywordById: selectKeywordState(state).keywordById,
  info: state.info.info,
  roleById: selectTrackRoles(state),
  bidOptionsById: selectBidState(state).bidOptions.byId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getSubmissionsAction: () => dispatch(submissionSlice.actions.GET_SUBMISSIONS()),
  deleteInfoAction: (data: InfoDeleteDTO) => dispatch(infoSlice.actions.DELETE(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionBidsPage);
